// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import awsconfig from './aws-exports';
import { useFileContext } from './ContextAPI/FileContext';
import { EC2LoadingProvider } from './contexts/EC2LoadingContext';
import { EC2InstanceStatusProvider } from './contexts/EC2InstanceStatusContext';
import cacheWarmingService from './services/cacheWarmingService';
import Navbar from './components/Navbar/Navbar';
import About from './components/pages/About';
import Services from './components/pages/Services';
import Contact from './components/pages/Contact';
import Home from './components/pages/Home';
import AccessibilityStatement from './components/AccessibilityStatement/AccessibilityStatement';
import LoginPage from './components/Login/LoginPage';
import AuthenticatedLayout from './components/auth';
import MagicFixer from './components/Sidebar/MagicFixer/MagicFixer';
import RSAAnalysis from './components/Sidebar/RSAAnalysis/RSAAnalysis';
import UserManagement from './components/UserManagement/UserManagement';
import Projects from './components/Projects/Projects';
import TestRoute from './components/TestRoute';
import BackgroundPreloader from './components/BackgroundPreloader';
import ScrollToTop from './components/ScrollToTop';
import { ProjectProvider } from './contexts/ProjectContext';
import DownloadsPopup from './components/Downloads/DownloadsPopup';
import PermissionGate from './components/PermissionGate/PermissionGate';
import OAuthCallback from './components/OAuthCallback/OAuthCallback';
import LoginModal from './components/Login/LoginModal';
import { setupAxiosInterceptors, setSessionExpiredCallback, resetSessionExpiredState, startSessionCheck, stopSessionCheck } from './utils/axiosInterceptor';
import './App.css';

// Add debugging to intercept all fetch/axios calls globally
(() => {
  const originalFetch = window.fetch;
  window.fetch = (...args) => {
    const url = args[0];
    if (typeof url === 'string' && url.includes('null')) {
      console.trace('🔍 GLOBAL NULL FETCH CALL DETECTED:', url);
      console.trace('🔍 Stack trace:', new Error().stack);
    }
    return originalFetch(...args);
  };
  
  // Also intercept axios if it's available
  if (window.axios) {
    const originalAxiosGet = window.axios.get;
    window.axios.get = (...args) => {
      const url = args[0];
      if (typeof url === 'string' && url.includes('null')) {
        console.trace('🔍 GLOBAL NULL AXIOS GET CALL DETECTED:', url);
        console.trace('🔍 Stack trace:', new Error().stack);
      }
      return originalAxiosGet(...args);
    };
  }
})();

// Configure Amplify
Amplify.configure(awsconfig);

// Debug: Check if Amplify is configured
console.log('Amplify configured:', awsconfig);

// Helper function to check if token exists
const hasToken = () => {
  try {
    // Check sessionStorage for token or auth flag
    const token = sessionStorage.getItem('token');
    const auth = sessionStorage.getItem('auth');
    
    // If we have a token or auth is set to 'true', consider it valid
    if (token || auth === 'true') {
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking token:', error);
    return false;
  }
};

// Helper function to get redirect URL based on current hostname
const getRedirectUrl = () => {
  const hostname = window.location.hostname;
  
  // If on 123bimlive.com (without www), redirect to www.123bimlive.com
  if (hostname === '123bimlive.com') {
    return 'https://www.123bimlive.com';
  }
  
  // If on www.123bimlive.com, redirect to homepage
  if (hostname === 'www.123bimlive.com') {
    return 'https://www.123bimlive.com';
  }
  
  // Default: redirect to www.123bimlive.com
  return 'https://www.123bimlive.com';
};

// Helper function to check if current path is a protected route
const isProtectedRoute = (pathname) => {
  const publicRoutes = ['/', '/about', '/services', '/contact', '/accessibility-statement', '/login'];
  return !publicRoutes.includes(pathname);
};

// Component to handle immediate redirect for protected routes
// This is a fallback safety net (synchronous check should catch routes first)
const RedirectToHome = () => {
  // Redirect immediately in component body (synchronous, not in useEffect)
  const redirectUrl = getRedirectUrl();
  console.log('🚫 RedirectToHome component redirecting to:', redirectUrl);
  window.location.href = redirectUrl;
  return null;
};

const App = () => {
  const { auth, authLoading } = useFileContext(); // Get auth and loading gate from FileContext
  const [logs, setLogs] = useState([]);
  const [showSessionExpired, setShowSessionExpired] = useState(false);

  // Setup axios interceptors once on mount
  useEffect(() => {
    setSessionExpiredCallback(() => setShowSessionExpired(true));
    setupAxiosInterceptors();
  }, []);

  // Start/stop background session check based on auth state
  useEffect(() => {
    if (auth) {
      resetSessionExpiredState();
      setShowSessionExpired(false);
      startSessionCheck();
    } else {
      stopSessionCheck();
    }
    return () => stopSessionCheck();
  }, [auth]);

  const handleSessionExpiredClose = () => {
    setShowSessionExpired(false);
    resetSessionExpiredState();
  };

  // Debug authentication state
  console.log('🔍 App.js - Auth Debug:', { auth, authLoading, location: window.location.pathname });
  
  // Debug authentication state changes
  useEffect(() => {
    console.log('🔍 App.js - Auth state changed:', { auth, authLoading, location: window.location.pathname });
  }, [auth, authLoading]);

  // Initialize cache warming when app loads
  useEffect(() => {
    console.log('🔥 App.js - Starting cache warming service...');
    cacheWarmingService.startCacheWarming();
  }, []);

  useEffect(() => {
    const { pathname, search, hash } = window.location;
    const normalizedPathname = pathname.replace(/\/{2,}/g, '/');
    if (normalizedPathname !== pathname) {
      const normalizedUrl = `${normalizedPathname}${search}${hash}`;
      console.warn('Normalizing malformed URL before route handling:', `${pathname}${search}${hash}`, '->', normalizedUrl);
      window.history.replaceState(window.history.state, '', normalizedUrl);
    }
  }, []);

  if (authLoading) {
    return (
      <Router>
        <ScrollToTop />
        <BackgroundPreloader />
        <div className='app' />
      </Router>
    );
  }

  // Early redirect check: If no token and on protected route, redirect immediately
  // BUT: Wait for authLoading to complete and check sessionStorage directly
  // This prevents redirecting when auth is still loading or sessionStorage hasn't synced
  const pathname = window.location.pathname;
  const tokenExists = hasToken();
  
  // Check sessionStorage directly as fallback (in case FileContext auth state hasn't updated yet)
  let sessionAuth = false;
  try {
    const storedAuth = sessionStorage.getItem('auth');
    sessionAuth = storedAuth === 'true' || storedAuth === true;
  } catch (e) {
    // sessionStorage might not be available
  }
  
  // Debug logging
  console.log('🔍 App.js Auth Check:', {
    pathname,
    auth,
    authLoading,
    tokenExists,
    sessionAuth,
    hasAnyAuth: auth || sessionAuth || tokenExists
  });
  
  // Only redirect if:
  // 1. Auth loading is complete (authLoading === false)
  // 2. No token in sessionStorage (tokenExists === false)
  // 3. No auth state from context AND no auth in sessionStorage (!auth && !sessionAuth)
  // 4. Route is protected
  if (!authLoading && !tokenExists && !auth && !sessionAuth && isProtectedRoute(pathname)) {
    console.log('🚫 No token found on protected route after auth check, redirecting to www.123bimlive.com');
    const redirectUrl = getRedirectUrl();
    window.location.href = redirectUrl;
    // Return early to prevent any rendering
    return null;
  }

  return (
    <EC2LoadingProvider>
      <EC2InstanceStatusProvider>
        <Router>
        <ScrollToTop />
        <BackgroundPreloader />
        <div className='app'>
        <Routes>
          {/* Show public routes only if NO authentication indicators exist */}
          {/* Show authenticated routes if ANY of: auth, sessionAuth, or tokenExists is true */}
          {(auth || sessionAuth || tokenExists) ? (
            <>
              <Route
                path='/'
                element={<Navigate to='/projects' replace />}
              />
              <Route
                path='/magic-fixer'
                element={
                  <AuthenticatedLayout>
                    <PermissionGate group="Admin" fallback={<Navigate to='/projects' replace />}>
                      <MagicFixer />
                    </PermissionGate>
                  </AuthenticatedLayout>
                }
              />
              {/* Single layout keeps ProjectProvider mounted when navigating list <-> project.
                  Previously three separate routes each mounted their own ProjectProvider; Back
                  navigated to /projects and unmounted/remounted the tree, double-running
                  loadProjects and risking auth/session races. */}
              <Route
                path='/projects'
                element={
                  <AuthenticatedLayout>
                    <ProjectProvider>
                      <Outlet />
                    </ProjectProvider>
                  </AuthenticatedLayout>
                }
              >
                <Route index element={<Projects />} />
                <Route path=':id' element={<Projects />} />
                <Route path=':id/view' element={<Projects />} />
              </Route>
              {/* RSA Analysis: no route-level gate — Setup/Paths/Dashboard tabs are gated inside RSAAnalysis */}
              <Route
                path='/RSA-Analysis'
                element={
                  <AuthenticatedLayout>
                    <RSAAnalysis />
                  </AuthenticatedLayout>
                }
              />
              <Route
                path='/user-management'
                element={
                  <AuthenticatedLayout>
                    <UserManagement />
                  </AuthenticatedLayout>
                }
              />
              <Route
                path='/auto-report'
                element={
                  <AuthenticatedLayout>
                    <div>Auto Report Placeholder</div>
                  </AuthenticatedLayout>
                }
              />
              {/* OAuth callback route - accessible in popup */}
              <Route
                path='/oauth/callback'
                element={<OAuthCallback />}
              />
              <Route path='*' element={<Navigate to='/projects' replace />} />
            </>
          ) : (
            <>
              {/* Public routes - shown when user is not authenticated */}
              {/* OAuth callback route - accessible in popup (public) */}
              <Route
                path='/oauth/callback'
                element={<OAuthCallback />}
              />
              <Route path='/login' element={<LoginPage />} />
              <Route
                path='/'
                element={
                  <>
                    <Navbar />
                    <Home />
                  </>
                }
              />
              <Route
                path='/about'
                element={
                  <>
                    <Navbar />
                    <About />
                  </>
                }
              />
              <Route
                path='/services'
                element={
                  <>
                    <Navbar />
                    <Services />
                  </>
                }
              />
              <Route
                path='/contact'
                element={
                  <>
                    <Navbar />
                    <Contact />
                  </>
                }
              />
              <Route
                path='/accessibility-statement'
                element={
                  <>
                    <AccessibilityStatement />
                  </>
                }
              />
              {/* Redirect protected routes immediately when no auth */}
              <Route
                path='/projects/:id'
                element={
                  <RedirectToHome />
                }
              />
              <Route
                path='/projects/:id/view'
                element={
                  <RedirectToHome />
                }
              />
              <Route path='*' element={<Navigate to='/' replace />} />
            </>
          )}
        </Routes>
      </div>

      {/* Re-authentication modal: shown when Cognito session is genuinely expired */}
      <LoginModal
        open={showSessionExpired}
        handleClose={handleSessionExpiredClose}
        onSuccess={handleSessionExpiredClose}
      />

      {/* Display the live logs */}
      {/* <div className="log-viewer">
        <h2>Live Logs</h2>
        <pre>
          {logs.map((log, index) => (
            <p key={index}>{log.timestamp} - {log.service}: {log.log}</p>
          ))}
        </pre>
      </div> */}
      <DownloadsPopup />
        </Router>
      </EC2InstanceStatusProvider>
    </EC2LoadingProvider>
  );
};

export default App;
